import * as Sentry from '@sentry/react';

type FeedbackIntegration = NonNullable<ReturnType<typeof Sentry.getFeedback>>;
export type SentryFeedbackOverrides = Parameters<FeedbackIntegration['createForm']>[0];

/**
 * Opens the Sentry user-feedback form, owning its lifecycle: the form is created,
 * appended to the DOM, and opened, then removed again on close or submit so a fresh
 * node doesn't accumulate on every trigger. No-ops when the feedback integration
 * isn't registered (e.g. Sentry is disabled for the current environment).
 *
 * Shared by the entry points that open feedback programmatically — the command
 * palette action and the ⌘/ hotkey ({@link useSentryFeedbackHotkey}). The
 * "Report issue" navigation buttons use `getFeedback().attachTo()` instead.
 */
export const openSentryFeedbackForm = (optionOverrides?: SentryFeedbackOverrides): void => {
  const feedback = Sentry.getFeedback();
  if (!feedback) {
    return;
  }

  let form: Awaited<ReturnType<FeedbackIntegration['createForm']>> | undefined;
  const removeForm = (): void => form?.removeFromDom();

  void feedback
    .createForm({
      ...optionOverrides,
      onFormClose: () => {
        removeForm();
        optionOverrides?.onFormClose?.();
      },
      onFormSubmitted: () => {
        removeForm();
        optionOverrides?.onFormSubmitted?.();
      },
    })
    .then((created) => {
      form = created;
      form.appendToDom();
      form.open();
    })
    .catch((error: unknown) => {
      Sentry.captureException(error);
    });
};
